import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Download, Search, FileText, Save } from "lucide-react";
import { formatARS, groupAR } from "@/lib/money";

const money = (n) => formatARS(n); // Fix BR: $ 1.234.567,89
const MONTHS = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-AR") : "—");

// Solapa "Cliente" — misma info que Historial pero, al abrir un presupuesto,
// SOLO se muestran los 3 valores permitidos para el cliente final (valor de
// la hora, total sin IVA, precio final con IVA). Nunca costos/cargas/margen.
export function ClientTab({ token }) {
  const headers = { "X-Budget-Pin-Token": token };
  const [query, setQuery] = useState("");
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);
  // Fix BR (2026-11-03): validez y "El servicio incluye" editables en un presupuesto ya guardado.
  const [edit, setEdit] = useState(null); // { validity_days, includesText }
  const [savingEdit, setSavingEdit] = useState(false);
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/budget/${detail.id}`, {
        validity_days: Math.min(365, Math.max(1, Number(edit.validity_days) || 30)),
        client_includes: edit.includesText.split("\n").map((x) => x.trim()).filter(Boolean),
      }, { headers });
      setDetail(data);
      setEdit({ validity_days: data.validity_days, includesText: (data.client_includes || []).join("\n") });
      toast.success("Presupuesto actualizado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingEdit(false); }
  };

  const load = async (q) => {
    try {
      const { data } = await api.get("/budget/list", { headers, params: q ? { q } : {} });
      setList(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const search = (e) => { e.preventDefault(); load(query); };

  const view = async (id) => {
    try {
      const { data } = await api.get(`/budget/${id}/client-view`, { headers });
      setDetail(data);
      setEdit({ validity_days: data.validity_days || 30, includesText: (data.client_includes || []).join("\n") });
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/budget/${id}/client.pdf`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `presupuesto_cliente_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: detail ? "1fr 1fr" : "1fr", gap: 18 }}>
      <div>
        <form onSubmit={search} style={{ display: "flex", gap: 8, marginBottom: 12 }} data-testid="budget-client-search-form">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre de objetivo o número..." data-testid="budget-client-search-input" style={{ flex: 1 }}/>
          <button type="submit" className="btn-ghost" style={{ padding: "8px 14px" }} data-testid="budget-client-search-btn"><Search size={14}/></button>
        </form>
        <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="budget-client-list">
          {list == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
          {list && list.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>No se encontraron presupuestos.</div>}
          {list && list.map((b) => (
            <div key={b.id} onClick={() => view(b.id)} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", cursor: "pointer", display: "flex", gap: 10, alignItems: "center" }} data-testid={`budget-client-row-${b.id}`}>
              <FileText size={16} color="var(--invict-red)"/>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Nº {b.number ?? "—"} · {b.name}</div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{b.objective_label} · {MONTHS[b.month]} {b.year}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {detail && (
        <div className="card" style={{ padding: 20 }} data-testid="budget-client-detail">
          <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Presupuesto Nº {detail.number ?? "—"}</h3>
          <p style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "0 0 16px" }}>
            {detail.objective_label} · {MONTHS[detail.month]} {detail.year} · Válido hasta {fmtDate(detail.valid_until)}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Row label="Horas de servicio en el mes" value={`${groupAR(detail.total_hours_month) || "0"} h`} testid="budget-client-hours"/>
            <Row label="Valor de la hora sin IVA (todo incluido)" value={money(detail.hourly_rate)} testid="budget-client-hourly-rate"/>
            <Row label="Valor de la hora con IVA" value={money(detail.hourly_rate_con_iva)} testid="budget-client-hourly-rate-iva"/>
            <Row label="Total mensual sin IVA" value={money(detail.total_sin_iva)} testid="budget-client-total-sin-iva"/>
            <div style={{ borderTop: "2px solid #0A0A0A", paddingTop: 8, marginTop: 4, display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 900 }}>
              <span>PRECIO FINAL (con IVA)</span><span data-testid="budget-client-total-final">{money(detail.precio_final_con_iva)}</span>
            </div>
          </div>
          {(detail.service_shifts || []).length > 0 && (
            <div style={{ marginTop: 14 }} data-testid="budget-client-shifts">
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Servicio</div>
              {detail.service_shifts.map((s, i) => (
                <div key={i} style={{ fontSize: 12, padding: "3px 0" }}>{s.label} · {s.time_from}–{s.time_to} · {s.staff_count} vigilador(es) · {s.days_label}</div>
              ))}
            </div>
          )}
          {edit && (
            <div style={{ marginTop: 14 }} data-testid="budget-client-edit">
              <div className="field"><label>Válido por (días)</label>
                <input type="number" min="1" max="365" value={edit.validity_days} onChange={(e) => setEdit({ ...edit, validity_days: e.target.value })} data-testid="budget-client-validity"/>
              </div>
              <div className="field"><label>El servicio incluye (uno por renglón, sin montos)</label>
                <textarea rows={7} value={edit.includesText} onChange={(e) => setEdit({ ...edit, includesText: e.target.value })} data-testid="budget-client-includes-edit"/>
              </div>
              <button type="button" onClick={saveEdit} disabled={savingEdit} className="btn-ghost" style={{ padding: "9px 14px" }} data-testid="budget-client-save-edit">
                <Save size={13} style={{ display: "inline", marginRight: 6 }}/>{savingEdit ? "Guardando..." : "Guardar validez y lista"}
              </button>
            </div>
          )}
          <button type="button" onClick={() => downloadPdf(detail.id)} className="btn-primary" style={{ marginTop: 18, padding: "11px 16px" }} data-testid={`budget-client-download-pdf-${detail.id}`}>
            <Download size={14} style={{ display: "inline", marginRight: 6 }}/>Descargar PDF para el cliente
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }} data-testid={testid}>
      <span style={{ color: "var(--invict-text-muted)" }}>{label}</span>
      <span style={{ fontWeight: 700, color: "#0A0A0A" }}>{value}</span>
    </div>
  );
}
